import React, { useEffect, useState } from "react";
import { PrimaryButton, DefaultButton, TextField, Stack, Persona, PersonaPresence, getTheme, Text, Separator, Spinner, FontIcon, Slider, ChoiceGroup } from "@fluentui/react";
// import your existing PeoplePicker and TagPicker-ish components

import { Chip, ExpandableText } from "./ProposalDashboard.js";
import { styles } from "./ProjectDashboard.js";

export default function BioBuilder({ proposalId }) {
  const [personId, setPersonId] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [generatedBio, setGeneratedBio] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [responseMeta, setResponseMeta] = useState(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [length, setLength] = useState(250);
  const [perspective, setPerspective] = useState("Third Person");

  const canGenerate = personId && selectedTagIds.length > 0;

  async function handleGenerate() {
    setIsLoading(true);
    setError("");
    setGeneratedBio("");

    try {
      const res = await fetch("/api/people/generate-bio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          personId,
          tagIds: selectedTagIds,
          proposalId: proposalId || null,
          context: additionalContext,
          length,
          perspective
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to generate bio");
      }

      const data = await res.json();
      setGeneratedBio(data.bio || "");
      setResponseMeta(data.meta || null);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveBio() {
    if (!generatedBio || !personId) return;

    try {
      const res = await fetch("/api/person_bio_variations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          person_id: personId,
          proposal_id: proposalId || null,
          bio_text: generatedBio,
          tag_ids: selectedTagIds,
          context: additionalContext
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save bio");
      }

      // Optional toast/snackbar here
      alert("Bio saved as a new variation.");
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save bio");
    }
  }

  const fetchAllTags = async () => {
    try {
      const res = await fetch(`/api/tags`);
      const data = await res.json();
      setAllTags(data);
      // return data;
    } catch (e) {
      console.error(e);
      // return [];
    }
  }

  const fetchAllPeople = async () => {
    try {
      const res = await fetch(`/api/people`, {params: {active: true}});
      const data = await res.json();
      setAllPeople(data.data.filter(el => el.active));
      // return data;
    } catch (e) {
      console.error(e);
      // return [];
    }
  }

  useEffect(() => {
    fetchAllTags();
    fetchAllPeople();
  }, []);

  return (

    <Stack tokens={{ childrenGap: 16 }} styles={{root: { padding: 24, maxWidth: 1200, margin: '0 auto' }}}>
      <Text variant="xxLarge">
        <FontIcon iconName="ChatBot" style={{marginRight: 8}} />
        Bio Builder
      </Text>
      <Text>
        This tool was built to help you generate a proposal ready bio drawing from previously written bios and any additional context you wish to provide. Chat GPT will use the gpt-4.1-mini model to generate a response. After generating, please check the "System" and "User Prompts" for errors. You can adjust your query and regenerate the response as necessary.
      </Text>
      <Separator />
      {/* Person selector */}
      <Stack.Item grow={1} className={styles.sectionCard}>
        <Text variant="xLarge">Who is this for?</Text>
        <Separator />
        <Stack horizontal wrap tokens={{childrenGap: 8}}>
          {
            allPeople.map(person => {
              return <Stack.Item key={person.id}>
                <Persona 
                  styles={{
                    root: {
                      cursor: 'pointer', 
                      background: personId === person.id ? `${getTheme().palette.green}30` : 'none',
                      border: `1px solid ${personId === person.id ? getTheme().palette.green : getTheme().palette.neutralLight}`,
                      padding: 4,
                      height: 'auto',
                      borderRadius: 48
                    }
                  }} 
                  onClick={() => setPersonId(person.id)} 
                  imageUrl={person.image_url} 
                  presence={personId === person.id ? PersonaPresence.online : PersonaPresence.none} text={person.name} />
              </Stack.Item>
            })
          }
        </Stack>
      </Stack.Item>

      {/* Tag selector */}
      <Stack.Item grow={1} className={styles.sectionCard}>
        <Stack tokens={{childrenGap: 8}}>
          <Text variant="xLarge">What should the bio focus on?</Text>
          <Text>
            The Bio Builder will fetch any past bio variations with the selected tags to provide as context before Ai generates a new bio. If no matching bios are found, it will pull from all previously created bios regardless of context.
          </Text>
        </Stack>
        <Separator />
        <Stack horizontal wrap tokens={{childrenGap: 8}}>
          {
            allTags.map(tag => {
              return <Chip key={tag.id} style={{padding: '4px 12px'}} size={16} primary={selectedTagIds.includes(tag.id)} onClick={() => setSelectedTagIds(prev => prev.includes(tag.id) ? prev.filter(el => el !== tag.id) : prev.concat(tag.id))}>{tag.name}</Chip>
            })
          }
        </Stack>
      </Stack.Item>

      {/* More Context / Settings */}
      <Stack horizontal tokens={{childrenGap: 16}}>
        <Stack.Item grow={1} className={styles.sectionCard}>
          <Text variant="xLarge">Any additional context?</Text>
          <Separator />
          <TextField 
            borderless 
            value={additionalContext} 
            placeholder="Add your own additional context here. Or leave blank." 
            onChange={(e, v) => setAdditionalContext(v)} 
            multiline 
            rows={4} />
        </Stack.Item>
        <Stack.Item grow={1} className={styles.sectionCard}>
          <Text variant="xLarge">Response Settings</Text>
          <Separator />
          <Slider value={length} label="Length" onChange={(val) => setLength(val)} min={100} max={1000} step={50} />
          <ChoiceGroup 
            label="Perspective"
            onChange={(e, o) => setPerspective(o.key)}
            selectedKey={perspective}
            options={[{key: "First Person", text: "First Person"}, {key: "Third Person", text: "Third Person"}]} />
        </Stack.Item>
      </Stack>

      <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="center">
        <PrimaryButton
          iconProps={{iconName: "Robot"}}
          styles={{root: {height: 42, fontSize: 18}, icon: {fontSize: 24}}}
          text={isLoading ? "Generating..." : "Generate Bio with AI"}
          onClick={handleGenerate}
          disabled={!canGenerate || isLoading}
        />
        {/* <DefaultButton
          text="Save as Bio Variation"
          onClick={handleSaveBio}
          disabled={!generatedBio}
        /> */}
      </Stack>

      {error && <div style={{ color: "salmon" }}>{error}</div>}

      {
        generatedBio && <Stack className={styles.sectionCard}>
          <Text variant="xLarge">Generated Bio</Text>
          <Separator />
          <TextField
            multiline
            rows={10}
            value={generatedBio}
            onChange={(_, v) => setGeneratedBio(v || "")}
            placeholder="The generated bio will show up here. You can edit it before saving."
          />
        </Stack>
      }
      

      {
        responseMeta && (
          <Stack horizontal tokens={{childrenGap: 12}}>
            <Stack.Item grow={1} className={styles.sectionCard} >
              <Text variant="xLarge">User Prompt</Text>
              <Separator />
              <ExpandableText text={responseMeta.userPrompt || "Not available"} />
            </Stack.Item>
            <Stack.Item grow={1} className={styles.sectionCard} >
              <Text variant="xLarge">System Prompt</Text>
              <Separator />
              <ExpandableText text={responseMeta.systemPrompt || "Not available"} />
            </Stack.Item>
          </Stack>
        )
      }
    </Stack>
  );
}
